import moment from 'moment';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { CellProps } from 'react-table';
import { AppContext } from '../../../../appContext/AppContext';
import { TableCustomCell } from '../../../../component/table/components/TableCustomCell';
import { TableCustomHeader } from '../../../../component/table/components/TableCustomHeader';
import { TableCustom } from '../../../../component/table/table-custom/TableCustom';
import { APP_DATE_FORMAT, CODE } from '../../../../utils/Constant';
import { convertToStringWithDelimiter, formatDateTime } from '../../../../utils/FormatUtils';
import { getHealthInsuranceCards } from '../../../services/TiepNhanServices';

type Props = {
	open: boolean;
	onClose: () => void;
	patientId: string;
};

const InsuranceCardsDialog = ({ open, onClose, patientId }: Props) => {
	const columns = useMemo(() => {
		return [
			{
				Header: (props: any) => (
					<TableCustomHeader
						tableProps={props}
						title="STT"
						className="text-center text-light min-w-40"
					/>
				),
				id: 'STT',
				Cell: ({ ...props }) => (
					<TableCustomCell className="text-center" data={props.row.index + 1} />
				),
			},
			{
				Header: (props: any) => (
					<TableCustomHeader
						tableProps={props}
						title="Mã thẻ"
						className="text-center text-light min-w-100px"
					/>
				),
				id: 'insCode',
				Cell: ({ row }: CellProps<any>) => (
					<TableCustomCell
						className="text-center min-w-100px"
						data={row.original.insCode}
					/>
				),
			},
			{
				Header: (props: any) => (
					<TableCustomHeader
						tableProps={props}
						title="Hạn thẻ"
						className="text-center text-light min-w-200px"
					/>
				),
				id: 'Hạn thẻ',
				Cell: ({ row }: CellProps<any>) => (
					<TableCustomCell
						className="text-center min-w-200px"
						data={`${convertToDateString(
							row.original.insFromDay,
							row.original.insFromMonth,
							row.original.insFromYear
						)} - ${convertToDateString(
							row.original.insToDay,
							row.original.insToMonth,
							row.original.insToYear
						)}`}
					/>
				),
			},
			{
				Header: (props: any) => (
					<TableCustomHeader
						tableProps={props}
						title="Ngày thêm thẻ"
						className="text-center text-light min-w-125px"
					/>
				),
				id: 'Ngày thêm thẻ',
				Cell: ({ value }: CellProps<any>) => (
					<TableCustomCell
						className="text-center min-w-125px"
						data={formatDateTime(value, APP_DATE_FORMAT)}
					/>
				),
			},
			{
				Header: (props: any) => (
					<TableCustomHeader
						tableProps={props}
						title="Nơi KCBBĐ"
						className="text-center text-light min-w-250px"
					/>
				),
				id: 'insKcbbdName',
				Cell: ({ row }: CellProps<any>) => (
					<TableCustomCell
						className="min-w-250px"
						data={convertToStringWithDelimiter([
							row.original.insKcbbdCode,
							row.original.insKcbbdName,
						])}
					/>
				),
			},
			{
				Header: (props: any) => (
					<TableCustomHeader
						tableProps={props}
						title="Tuyến KCB"
						className="text-center text-light min-w-250px"
					/>
				),
				id: 'Tuyến KCB',
				Cell: ({ row }: CellProps<any>) => (
					<TableCustomCell
						className="text-center min-w-250px"
						data={convertToStringWithDelimiter([
							row.original.insLevelName,
							row.original.insCoveragePercentage,
						])}
					/>
				),
			},
			{
				Header: (props: any) => (
					<TableCustomHeader
						tableProps={props}
						title="Ngày miễn cùng chi trả"
						className="text-center text-light min-w-175px"
					/>
				),
				id: 'Ngày miễn cùng chi trả',
				Cell: ({ row: { original } }: CellProps<any>) => (
					<TableCustomCell
						className="text-center min-w-175px"
						data={convertToDateString(
							original?.insNgaymienchitraDay,
							original?.insNgaymienchitraMonth,
							original?.insNgaymienchitraYear
						)}
					/>
				),
			},
			{
				Header: (props: any) => (
					<TableCustomHeader
						tableProps={props}
						title="Ngày đủ 5 năm liên tục"
						className="text-center text-light min-w-175px"
					/>
				),
				id: 'Ngày đóng đủ 5 năm',
				Cell: ({ row: { original } }: CellProps<any>) => (
					<TableCustomCell
						className="text-center min-w-175px"
						data={convertToDateString(
							original?.insNgaydu5namDay,
							original?.insNgaydu5namMonth,
							original?.insNgaydu5namYear
						)}
					/>
				),
			},
		];
	}, []);

	const { setIsLoading } = useContext(AppContext);
	const [data, setData] = useState([]);

	useEffect(() => {
		if (patientId) {
			getTableData(patientId);
		}
	}, []);

	const getTableData = async (patientId: string) => {
		try {
			setIsLoading(true);
			const { code, data } = await getHealthInsuranceCards({
				patientId,
			});

			if (CODE.SUCCESS !== code) {
				throw new Error(data);
			}

			setData(data.content);
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	const convertToDateString = (day: number, month: number, year: number) => {
		if (!year) return '';
		
		return moment([year, month - 1, day]).format('DD/MM/YYYY');
	};

	return (
		<Modal
			dialogClassName="h-modal"
			size="xl"
			show={open}
			onHide={onClose}
			backdrop="static"
			centered
		>
			<Modal.Header closeButton>
				<Modal.Title>Danh sách thẻ BHYT</Modal.Title>
			</Modal.Header>

			<Modal.Body className='min-h-200px'>
				<TableCustom data={data} columns={columns} maxHeight={250} minHeight={200} />
			</Modal.Body>

			<Modal.Footer>
				<Button className="btn-danger" onClick={onClose}>
					Đóng
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default InsuranceCardsDialog;
